import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { User } from 'firebase/auth'
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth'
import { auth, googleProvider } from '../lib/firebase'
import { ensureBootstrapped } from '../org/bootstrap'

type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; user: User; personalOrgId: string }
  | { status: 'error'; message: string }

interface AuthContextValue {
  state: AuthState
  signIn: () => Promise<void>
  signOutNow: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' })

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      // An anonymous identity is not a sign-in as far as this app is concerned.
      // It exists only so the public share route can read timing, which sits
      // behind signedIn() as a cost control.
      //
      // Treated as signed out deliberately, and not merely cosmetically: the
      // bootstrap below creates a user document, a personal organisation and a
      // membership. Running it for every share-link visitor would leave three
      // permanent documents per browser, and would have the public route —
      // which is built to have no path to organisation data — creating some.
      if (!user || user.isAnonymous) {
        setState({ status: 'signed-out' })
        return
      }
      setState({ status: 'loading' })
      ensureBootstrapped(user)
        .then((personalOrgId) => setState({ status: 'signed-in', user, personalOrgId }))
        .catch((error: unknown) =>
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : 'Could not set up your account.',
          }),
        )
    })
  }, [])

  async function signIn() {
    try {
      await signInWithPopup(auth, googleProvider)
    } catch (error: unknown) {
      // Closing the popup is a normal thing to do, not an error worth showing.
      const code = (error as { code?: string })?.code
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return
      setState({
        status: 'error',
        message: error instanceof Error ? error.message : 'Sign-in failed.',
      })
    }
  }

  async function signOutNow() {
    await signOut(auth)
  }

  return (
    <AuthContext.Provider value={{ state, signIn, signOutNow }}>{children}</AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
